import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALITEM_LOAD_BY_ID,
    ACTION_TRIALITEMS_LOAD_BY_TRIAL_ID,
    ACTION_TRIALITEM_LOAD_FAILURE,
    ACTION_TRIALITEM_LOAD_SUCCESS,

    ACTION_TRIALITEM_SAVE,
    ACTION_TRIALITEM_SAVE_SUCCESS,
    ACTION_TRIALITEM_SAVE_FAILURE,

    ACTION_TRIALITEM_CREATE,
    ACTION_TRIALITEM_CREATE_SUCCESS,
    ACTION_TRIALITEM_CREATE_FAILURE,

    ACTION_TRIALITEM_CLEAR,
    ACTION_TRIALITEM_FILTER,

    TrialItemAction
} from '../actions/trialItem';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from 'src/helpers/formErrorHelper';


export interface ITrialItemState {
    formData: Dtos.TrialItem | Dtos.TrialItem[],
    formDataTrialed: Dtos.TrialItemCategory[];
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialItem[];
    formFilteredDataTrialed: Dtos.TrialItemCategory[];
}

const initialState: ITrialItemState = {
    formData: undefined,
        formDataTrialed: undefined,
        formState: undefined,
        formProperties: undefined,
        validationFailures: undefined,
        lookups: undefined,
        loadState: {
            status: RequestState.None
        } as IRequestState,
        saveState: {
            status: RequestState.None
        } as IRequestState,
        updateState: {
            status: RequestState.None
        } as IRequestState,
        createState: {
            status: RequestState.None
        } as IRequestState,
        formFilter: {
            search: "",
            showInActive: false
        },
        formFilteredData: [],
        formFilteredDataTrialed: undefined,
};


function loading(state: ITrialItemState, _isLoading: boolean): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialItemState,
    formData: Dtos.TrialItem | Dtos.TrialItem[],
    formDataTrialed: Dtos.TrialItemCategory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        formData: {
            $set: formData
        },
        formDataTrialed: {
            $set: formDataTrialed
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: ITrialItemState, formFilter: IFormFilter): ITrialItemState {

    const formData: Dtos.TrialItem[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = formData ? formData.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p?.trialItemTemplate?.name?.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    let formFilteredDataTrialed = [];
    if (state.formDataTrialed) {
        for (var i = 0; i < state.formDataTrialed.length; i++) {
            const filteredCategoryItems =
                state.formDataTrialed[i].trialItems.filter(p => (p.active == true || formFilter.showInActive) &&
                    (formFilter.search == "" ||
                        (p?.trialItemTemplate?.name != undefined && p?.trialItemTemplate?.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1) ||
                        (p?.trialItemTemplate?.description != undefined && p?.trialItemTemplate?.description.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
                    ));
            if (filteredCategoryItems.length > 0) {
                formFilteredDataTrialed.push({
                    ...state.formDataTrialed[i],
                    trialItems: filteredCategoryItems
                });
            }
        }
    }

    const newState: ITrialItemState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formFilteredDataTrialed: {
            $set: formFilteredDataTrialed
        }
    });

    return newState;
}

function loadFailure(state: ITrialItemState, responseStatus: Dtos.ResponseStatus): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: ITrialItemState, propertiesToClear?: string[]): ITrialItemState {

    if (!propertiesToClear) {
        const newState: ITrialItemState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialItemState, _isSaving: boolean): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialItemState,
    formData: Dtos.TrialItem | Dtos.TrialItem[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialItemState, responseStatus: Dtos.ResponseStatus): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialItemState, _isCreating: boolean): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialItemState,
    formData: Dtos.TrialItem,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialItemState, responseStatus: Dtos.ResponseStatus): ITrialItemState {
    const newState: ITrialItemState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialItemReducer: Reducer<ITrialItemState> = (state: ITrialItemState = initialState, action: TrialItemAction) => {
    switch (action.type) {
        case ACTION_TRIALITEM_LOAD_BY_ID:
        case ACTION_TRIALITEMS_LOAD_BY_TRIAL_ID:
            return loading(state, true);
        case ACTION_TRIALITEM_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formDataTrialed, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALITEM_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALITEM_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALITEM_SAVE:
            return saving(state, true);
        case ACTION_TRIALITEM_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALITEM_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALITEM_CREATE:
            return creating(state, true);
        case ACTION_TRIALITEM_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALITEM_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALITEM_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default TrialItemReducer;